import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Tag, Space, message, Button, Select } from 'antd';
import { BankOutlined, FilterOutlined } from '@ant-design/icons';
import { getChannelLogo } from '../utils/channelLogos';
import { useLanguage } from '../contexts/LanguageContext';

export default function ChannelAccountManagement() {
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [filterChannel, setFilterChannel] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const { t } = useLanguage();

  const channelOptions = useMemo(() => {
    const codes = [...new Set(accounts.map(a => a.channelCode).filter(Boolean))];
    return codes.map(c => ({ label: c, value: c }));
  }, [accounts]);

  const filteredAccounts = useMemo(() => accounts.filter(a => {
    if (filterChannel && a.channelCode !== filterChannel) return false;
    if (filterStatus !== null && a.status !== filterStatus) return false;
    return true;
  }), [accounts, filterChannel, filterStatus]);

  useEffect(() => { loadAccounts(); }, []);

  const getCurrentUser = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) { try { return JSON.parse(userStr).username; } catch (e) {} }
    const tokenStr = localStorage.getItem('token');
    if (tokenStr) { try { return JSON.parse(atob(tokenStr.split('.')[1])).sub; } catch (e) {} }
    return 'admin';
  };

  const callApi = async (testType, body = {}) => {
    const response = await fetch('/inner/v1/fiat-payment/test/execute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ testType, executor: getCurrentUser(), ...body })
    });
    return response.json();
  };

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const result = await callApi('CHANNEL_ACCOUNTS_LIST');
      if (result.code === '200') setAccounts(result.data || []);
      else message.error(result.msg || t('loadFailed'));
    } catch { message.error(t('loadFailed')); }
    finally { setLoading(false); }
  };

  const copyAccount = async (accountId) => {
    try {
      const result = await callApi('CHANNEL_ACCOUNT_COPY', { requestBody: JSON.stringify({ accountId }) });
      if (result.code === '200' || result.code === 200) { message.success(t('createSuccess')); loadAccounts(); }
      else message.error(result.msg || t('operationFailed'));
    } catch { message.error(t('operationFailed')); }
  };

  const activateAccount = async (accountId) => {
    try {
      const result = await callApi('CHANNEL_ACCOUNT_ACTIVATE', { requestBody: JSON.stringify({ accountId }) });
      if (result.code === '200' || result.code === 200) { message.success(t('enable') + t('success')); loadAccounts(); }
      else message.error(result.msg || t('operationFailed'));
    } catch { message.error(t('operationFailed')); }
  };

  const deactivateAccount = async (accountId) => {
    try {
      const result = await callApi('CHANNEL_ACCOUNT_DEACTIVATE', { requestBody: JSON.stringify({ accountId }) });
      if (result.code === '200' || result.code === 200) { message.success(t('disable') + t('success')); loadAccounts(); }
      else message.error(result.msg || t('operationFailed'));
    } catch { message.error(t('operationFailed')); }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: t('channelCode'), dataIndex: 'channelCode', key: 'channelCode', render: (text) => <Tag color="blue">{text}</Tag> },
    { title: t('clientType'), dataIndex: 'clientType', key: 'clientType', render: (text) => <Tag color={text === 'PAYIN' ? 'green' : 'orange'}>{text}</Tag> },
    { title: t('currency'), dataIndex: 'currency', key: 'currency', render: (text) => <Tag color="purple">{text}</Tag> },
    { title: 'BusinessPartner', dataIndex: 'businessPartner', key: 'businessPartner' },
    { title: t('userType'), dataIndex: 'userType', key: 'userType', render: (text) => <Tag color={text === 'BUSINESS' ? 'cyan' : 'geekblue'}>{text}</Tag> },
    { title: t('accountType'), dataIndex: 'accountType', key: 'accountType', render: (text) => <Tag color={text === 'MAIN' ? 'gold' : 'default'}>{text}</Tag> },
    { title: t('localAccountNo'), dataIndex: 'accountNo', key: 'accountNo' },
    { title: t('channelAccountNo'), dataIndex: 'channelAccountNo', key: 'channelAccountNo' },
    { title: t('customerId'), dataIndex: 'customerId', key: 'customerId' },
    { title: t('priority'), dataIndex: 'priority', key: 'priority' },
    {
      title: t('status'), dataIndex: 'status', key: 'status',
      render: (status) => status === 1 ? <Tag color="success">{t('active')}</Tag> : <Tag color="default">{t('disabled')}</Tag>
    },
    {
      title: t('operation'), key: 'action', width: 200,
      render: (_, record) => (
        <Space>
          {record.businessPartner !== 'DEMO_ACCOUNT' && (
            <Button type="primary" size="small" onClick={() => copyAccount(record.id)}>
              {t('copyToDemo')}
            </Button>
          )}
          {record.status === 1 ? (
            <Button size="small"
              style={{ background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)', border: 'none', color: 'white', borderRadius: '6px', boxShadow: '0 2px 4px rgba(238,90,82,0.3)', transition: 'all 0.3s ease' }}
              onMouseEnter={(e) => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 4px 8px rgba(238,90,82,0.4)'; }}
              onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 2px 4px rgba(238,90,82,0.3)'; }}
              onClick={() => deactivateAccount(record.id)}
            >🚫 {t('disable')}</Button>
          ) : (
            <Button size="small"
              style={{ background: 'linear-gradient(135deg, #4ecdc4, #44a08d)', border: 'none', color: 'white', borderRadius: '6px', boxShadow: '0 2px 4px rgba(78,205,196,0.3)', transition: 'all 0.3s ease' }}
              onMouseEnter={(e) => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 4px 8px rgba(78,205,196,0.4)'; }}
              onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 2px 4px rgba(78,205,196,0.3)'; }}
              onClick={() => activateAccount(record.id)}
            >✅ {t('enable')}</Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1600, margin: '0 auto' }}>
      <div className="fade-in-up">
        <Card
          className="modern-card"
          title={
            <Space size={16}>
              <BankOutlined style={{ color: '#667eea', fontSize: 20 }} />
              <span style={{ fontSize: 20, fontWeight: 600 }}>{t('channelAccountManagement')}</span>
            </Space>
          }
          extra={
            <Space size={12} style={{ background: '#f8f9fa', padding: '8px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
              <FilterOutlined style={{ color: '#667eea' }} />
              <Select allowClear placeholder={t('allChannels')} style={{ width: 180 }} onChange={setFilterChannel}
                options={channelOptions.map(o => ({
                  label: <Space size={6}><span>{getChannelLogo(o.value).emoji}</span><span>{getChannelLogo(o.value).name}</span></Space>,
                  value: o.value
                }))}
              />
              <Select allowClear placeholder={t('allStatuses')} style={{ width: 110 }} onChange={setFilterStatus}
                options={[
                  { label: <Tag color="success" style={{ margin: 0 }}>{t('active')}</Tag>, value: 1 },
                  { label: <Tag color="default" style={{ margin: 0 }}>{t('disabled')}</Tag>, value: 0 },
                ]}
              />
              <span style={{ color: '#8c8c8c', fontSize: 13 }}>{t('totalCount', filteredAccounts.length)}</span>
            </Space>
          }
        >
          <Table columns={columns} dataSource={filteredAccounts} rowKey="id" loading={loading}
            pagination={{ pageSize: 20 }} className="modern-table" scroll={{ x: 1400 }} />
        </Card>
      </div>
    </div>
  );
}
